// ============================================================
// ConditionsSection - the conditions block, once.
//
// This existed twice: as three <section> cards in ActorStatusTab and as
// inline-styled markup in TargetPanel's Conds tab. Same chips, same Add
// and Clear All, same critical-damage list - different colours, spacings
// and remove affordances (one had an ✕ button, the other made the whole
// chip clickable), because they were written months apart.
//
// The WRITERS stay with the panels. The left side goes through
// updateCombatantState, the right through updateTab; that difference is
// real and this component should not know about it. Same seam as
// CombatantSheetTabs.
// ============================================================
import { useState } from 'react';
import type { StackPolicy } from '../engine/stackPolicy';
import clsx from '../utils/clsx';
import AddConditionModal from './AddConditionModal';
import { condColor, decodeCond } from '../engine/conditions';
import { isExposureCode } from '../engine/exposures';
import type { Target } from '../types';
import { ConditionChip } from './ConditionChip';
import BadgeRow from './BadgeRow';
import common from '../styles/common.module.css';
import styles from '../styles/ConditionsSection.module.css';

export interface ConditionsSectionProps {
  target: Target;
  /** `opts.stacking` is the picker's own ruling for this one (v8.367.0). */
  onAdd: (code: string, opts?: { stacking?: StackPolicy }) => void;
  onRemove: (index: number) => void;
  onClear: () => void;
  locked?: boolean;
  /** Transient "trait removed these conditions" notice from the panel. */
  flash?: { traitLabel: string; removed: string[] } | null;
  className?: string;
  /** More verbs for the header row - the Expose button lives here (v8.356.2). */
  extraActions?: React.ReactNode;
}

export default function ConditionsSection({
  target,
  onAdd,
  onRemove,
  onClear,
  locked = false,
  flash = null,
  className,
  extraActions = null,
}: ConditionsSectionProps) {
  const [showAdd, setShowAdd] = useState(false);
  // EXPOSURES ARE NOT CONDITIONS (v8.351.0): an `Ex:` code lives in the
  // same list - a code, not a field, by ADR - but it is what OTHERS see,
  // and ExposuresSection shows it. Kept out here, with the ORIGINAL
  // indices, because the writers remove by index in the whole list.
  const conditions = conditionEntries(target.conditions);

  return (
    <div className={clsx(styles.wrap, locked && common.ariaDisabled, className)} aria-disabled={locked || undefined}>
      <ConditionsHeader
        count={conditions.length}
        locked={locked}
        onAdd={() => setShowAdd(true)}
        onClear={onClear}
        extraActions={extraActions}
      />
      {flash && <FlashNotice flash={flash} />}
      <ConditionList conditions={conditions} onRemove={locked ? null : onRemove} />
      <CriticalDamage crits={target.criticalDamage || []} />
      {/* The modal stays OPEN after applying a condition, and is closed by
          the user. The two copies disagreed here - the actor side closed
          on every add, the target side did not - and staying open is the
          right one: a bad hit lands Stunned AND Prone AND Blinded, and
          reopening the picker between each is three clicks of nothing. */}
      {showAdd && <AddConditionModal target={target} onAdd={onAdd} onClose={() => setShowAdd(false)} />}
    </div>
  );
}

/** The conditions in a list that are not exposures, each with its index in the whole list. */
function conditionEntries(conditions: readonly string[] | undefined): { code: string; index: number }[] {
  return (conditions || []).flatMap((code, index) => (isExposureCode(code) ? [] : [{ code, index }]));
}

/** "Astartes removed: Stunned" - keyed, so a new notice replays its animation. */
function FlashNotice({ flash }: { flash: { traitLabel: string; removed: string[] } }) {
  return (
    <div className={styles.flash} key={flash.traitLabel + flash.removed.join(',')} data-testid="cond-flash">
      {flash.traitLabel} removed: {flash.removed.join(', ')}
    </div>
  );
}

interface HeaderProps {
  count: number;
  locked: boolean;
  onAdd: () => void;
  onClear: () => void;
  extraActions: React.ReactNode;
}

/** "Conditions (n)", + Add, Clear All, and any extra verbs (the Expose button). */
function ConditionsHeader({ count, locked, onAdd, onClear, extraActions }: HeaderProps) {
  return (
    <div className={styles.header}>
      <span className={styles.heading}>Conditions ({count})</span>
      {/* data-ghost-hide: pure verbs - the actor curtain's glass
          projection hides them (informative only). */}
      <span className={styles.actions} data-ghost-hide="true">
        <button className={common.btnSmall} onClick={onAdd} disabled={locked} data-testid="add-condition-btn">
          + Add
        </button>
        {count > 0 && (
          <button className={clsx(common.btnSmall, styles.clear)} onClick={onClear} disabled={locked}>
            Clear All
          </button>
        )}
        {extraActions}
      </span>
    </div>
  );
}

interface ConditionListProps {
  conditions: { code: string; index: number }[];
  /** Removes by index in the whole list; null when locked. */
  onRemove: ((index: number) => void) | null;
}

function ConditionList({ conditions, onRemove }: ConditionListProps) {
  if (conditions.length === 0) return <div className={common.overviewEmpty}>No active conditions.</div>;
  const chips = conditions.map(({ code, index: i }) => (
    <ConditionChip
      key={`${code}-${i}`}
      colours={condColor(code)}
      label={decodeCond(code) ?? code}
      onRemove={onRemove && (() => onRemove(i))}
    />
  ));
  // One line: past it, "+N" opens the rest - so the plate above keeps its height (2026-10-24).
  return (
    <div className={styles.list} data-testid="conditions-list">
      <BadgeRow label="Conditions" full={chips}>
        {chips}
      </BadgeRow>
    </div>
  );
}

/** The critical damage taken, folded away. */
function CriticalDamage({ crits }: { crits: string[] }) {
  if (crits.length === 0) return null;
  return (
    <details className={styles.crits} data-testid="critical-damage">
      <summary className={styles.critSummary}>Critical Damage ({crits.length})</summary>
      <ul className={styles.critList}>
        {crits.map((c, i) => (
          <li key={i}>{c}</li>
        ))}
      </ul>
    </details>
  );
}
